import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { TestSetService } from "../../services/TestSetService";
import { usePermissions } from "../../PermissionsProvider";
import { IntentGroup, TestSet } from "../../model_gen/evaluation";
import { ChatCompletionRequest } from "../../model_gen/chat";
import { IntentService } from "../../services/IntentService";
import TestRequestEdit from "./TestRequestEdit";
import { RequestViewButton } from "../shared/button/Buttons";
import TestSetEligibleRequestsTable from "./TestSetEligibleRequestsTable";
import TestSetAddedRequestsTable from "./TestSetAddedRequestsTable";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { Tooltip } from "../ui/tooltip";
import { 
  PlusIcon,
  FileTextIcon,
  SearchIcon,
  XIcon,
  Loader2Icon,
  InfoIcon,
  ArrowUpDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon
} from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";

// Add new interface for tag distribution
interface TagDistribution {
  tag: string;
  count: number;
}

const TestSetEdit: React.FC = () => {
  const { intentGroupId, testSetId } = useParams();
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [intentGroup, setIntentGroup] = useState<IntentGroup | null>(null);
  const [testSet, setTestSet] = useState<TestSet | null>(null);
  
  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isGolden, setIsGolden] = useState(false);

  // Eligible requests state (similar to added requests)
  const [currentEligiblePage, setCurrentEligiblePage] = useState<number>(1);
  const [eligiblePageSize, setEligiblePageSize] = useState<number>(10);
  const [eligibleRequests, setEligibleRequests] = useState<ChatCompletionRequest[]>([]);
  const [totalEligible, setTotalEligible] = useState<number>(0);
  const [isEligibleLoading, setIsEligibleLoading] = useState<boolean>(false);
  const eligibleRequestsCacheRef = useRef<{ [page: number]: ChatCompletionRequest[]; }>({});

  // Added requests state
  const [currentAddedPage, setCurrentAddedPage] = useState<number>(1);
  const [addedPageSize, setAddedPageSize] = useState<number>(10);
  const [addedRequests, setAddedRequests] = useState<ChatCompletionRequest[]>([]);
  const [totalAdded, setTotalAdded] = useState<number>(0);
  const [isAddedLoading, setIsAddedLoading] = useState<boolean>(false);
  const addedRequestsCacheRef = useRef<{ [page: number]: ChatCompletionRequest[]; }>({});
  
  // Selection state
  const [selectedEligibleRequests, setSelectedEligibleRequests] = useState<ChatCompletionRequest[]>([]);
  const [selectedAddedRequests, setSelectedAddedRequests] = useState<ChatCompletionRequest[]>([]);
  
  // Tab and filter state
  const [activeTab, setActiveTab] = useState<string>("eligible");
  const [showOnlySelectedEligible, setShowOnlySelectedEligible] = useState(false);
  const [showOnlySelectedAdded, setShowOnlySelectedAdded] = useState(false);
  const [addedRefreshTrigger, setAddedRefreshTrigger] = useState(0);
  const [eligibleRefreshTrigger, setEligibleRefreshTrigger] = useState(0);
  const [tagDistribution, setTagDistribution] = useState<TagDistribution[]>([]);
  const [initialLoadDate] = useState<number>(Date.now());
  
  // Search functionality is now handled by the TestSetEligibleRequestsTable component

  useEffect(() => {
    if (intentGroupId) {
      IntentService.getIntentGroup(
        Number(intentGroupId),
        false,
        setIsLoading,
        apiKey
      ).subscribe((intentGroup: IntentGroup) => {
        setIntentGroup(intentGroup);
      });
    }
  }, [intentGroupId]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSet(testSetId, setIsLoading, apiKey).subscribe(
        (data: TestSet) => {
          setTestSet(data);
          setName(data.name || "");
          setDescription(data.description || "");
          setIsGolden(data.is_golden || false);

          TestSetService.getTestSetRequests(
            testSetId,
            -1,
            -1,
            0,
            10,
            null,
            apiKey
          ).subscribe(
            (response: { items: ChatCompletionRequest[] }) => {
              setAddedRequests(response.items);
              setIsLoading(false);
            },
            (error: any) => {
              console.error(error);
              setIsLoading(false);
            }
          );
        },
        (error: any) => {
          console.error(error);
          setIsLoading(false);
        }
      );
    }
  }, [testSetId]);

  // Fetch eligible requests
  useEffect(() => {
    if (intentGroupId) {
      fetchEligibleRequests(currentEligiblePage);
    }
  }, [currentEligiblePage, intentGroupId, eligibleRefreshTrigger, eligiblePageSize]);

  // Fetch added requests
  useEffect(() => {
    if (testSetId) {
      fetchAddedRequests(currentAddedPage);
    }
  }, [currentAddedPage, testSetId, addedRefreshTrigger, addedPageSize]);

  useEffect(() => {
    if (testSetId) {
      TestSetService.getTestSetRequestDistribution(
        testSetId,
        null,
        apiKey
      ).subscribe(
        (response: TagDistribution[]) => {
          setTagDistribution(response);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [testSetId, addedRefreshTrigger]);

  const fetchEligibleRequests = (page: number) => {
    if (eligibleRequestsCacheRef.current[page]) {
      setEligibleRequests(eligibleRequestsCacheRef.current[page]);
      return;
    }

    const offset = (page - 1) * eligiblePageSize;
    setIsEligibleLoading(true);

    const serviceCall = testSetId 
      ? TestSetService.getEligibleTestSetRequests(
          Number(intentGroupId),
          Number(testSetId),
          initialLoadDate,
          offset,
          eligiblePageSize,
          null,
          setIsEligibleLoading,
          apiKey
        )
      : TestSetService.getEligibleTestSetRequests(
          Number(intentGroupId),
          -1,
          initialLoadDate,
          offset,
          eligiblePageSize,
          null,
          setIsEligibleLoading,
          apiKey
        );

    serviceCall.subscribe(
      (data: any) => {
        const items = data.items || [];
        const totalCount = data.total || 0;

        setEligibleRequests(items);
        setTotalEligible(totalCount);
        eligibleRequestsCacheRef.current = {
          ...eligibleRequestsCacheRef.current,
          [page]: items,
        };
        setIsEligibleLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsEligibleLoading(false);
      }
    );
  };

  const fetchAddedRequests = (page: number) => {
    if (addedRequestsCacheRef.current[page]) {
      setAddedRequests(addedRequestsCacheRef.current[page]);
      return;
    }

    const offset = (page - 1) * addedPageSize;
    setIsAddedLoading(true);

    TestSetService.getTestSetRequests(
      testSetId!,
      -1,
      -1,
      offset,
      addedPageSize,
      null,
      apiKey
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setAddedRequests(response.items);
        setTotalAdded(response.total);
        addedRequestsCacheRef.current = {
          ...addedRequestsCacheRef.current,
          [page]: response.items,
        };
        setIsAddedLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsAddedLoading(false);
      }
    );
  };

  // Search functionality is now handled by the TestSetEligibleRequestsTable component

  const handleEligibleTagsChange = (record: ChatCompletionRequest, newTags: string[]) => {
    const updatedRequests = selectedEligibleRequests ? selectedEligibleRequests.map((row) => {
      if (row?.request_id === record?.request_id) {
        return {
          ...row,
          metadata: {
            ...row.metadata,
            _maitai_test_request_meta: JSON.stringify({
              tags: newTags,
            }),
          },
        };
      }
      return row;
    }) : [];
    setSelectedEligibleRequests(updatedRequests);
  };

  const handleTagsChange = (requestId: string, newTags: string[]) => {
    if (activeTab === "added") {
      setIsAddedLoading(true);

      TestSetService.updateTestSetRequestTags(
        testSetId!,
        requestId,
        newTags,
        null,
        apiKey
      ).subscribe(
        () => {
          setAddedRequests((prev) =>
            prev.map((r) => {
              if (r.request_id === requestId) {
                return {
                  ...r,
                  metadata: {
                    ...r.metadata,
                    _maitai_test_request_meta: JSON.stringify({
                      tags: newTags,
                    }),
                  },
                };
              }
              return r;
            })
          );
          setIsAddedLoading(false);
        },
        (error: any) => {
          console.error(error);
          setIsAddedLoading(false);
        }
      );
    } else {
      setSelectedEligibleRequests((prev) =>
        prev.map((row) => {
          if (row.request_id === requestId) {
            return {
              ...row,
              metadata: {
                ...row?.metadata,
                _maitai_test_request_meta: JSON.stringify({ tags: newTags }),
              },
            };
          }
          return row;
        })
      );
    }
  };

  const handleAddSelected = () => {
    setIsAddedLoading(true);
    const payload = {
      test_set_id: Number(testSetId),
      requests: selectedEligibleRequests.map((request) => ({
        request_id: request.request_id,
        metadata: request.metadata || {},
      })),
    };

    TestSetService.addTestSetRequests(payload, null, apiKey).subscribe(
      () => {
        // Clear caches
        addedRequestsCacheRef.current = {};
        eligibleRequestsCacheRef.current = {};
        
        setSelectedEligibleRequests([]);
        setCurrentAddedPage(1);
        setCurrentEligiblePage(1);
        setAddedRefreshTrigger((prev) => prev + 1);
        setEligibleRefreshTrigger((prev) => prev + 1);
        setIsAddedLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsAddedLoading(false);
      }
    );
  };

  const handleRemoveSelected = () => {
    setIsAddedLoading(true);

    const payload = {
      test_set_id: Number(testSetId),
      requests: selectedAddedRequests.map((request) => ({
        request_id: request.request_id,
        metadata: request.metadata || {},
      })),
    };

    TestSetService.removeTestSetRequests(payload, null, apiKey).subscribe(
      () => {
        // Clear caches
        addedRequestsCacheRef.current = {};
        eligibleRequestsCacheRef.current = {};
        
        setSelectedAddedRequests([]);
        setCurrentAddedPage(1);
        setCurrentEligiblePage(1);
        setAddedRefreshTrigger((prev) => prev + 1);
        setEligibleRefreshTrigger((prev) => prev + 1);
        setIsAddedLoading(false);
      },
      (error: any) => {
        console.error(error);
        setIsAddedLoading(false);
      }
    );
  };

  const handleSave = async () => {
    const hasSelectedRows = selectedEligibleRequests.length > 0 || selectedAddedRequests.length > 0;
    if (hasSelectedRows) {
      return;
    }
    
    try {
      setIsSaving(true);

      const payload = {
        id: Number(testSetId),
        name: name,
        description: description,
        is_golden: isGolden,
        company_id: testSet?.company_id,
        intent_group_id: Number(intentGroupId),
        date_created: testSet?.date_created,
        meta: testSet?.meta,
      };

      TestSetService.updateTestSet(payload, setIsSaving, apiKey).subscribe(
        () => {
          navigate(`/intent-group/${testSet.intent_group_id}/testsets/${testSetId}`);
        },
        (error: any) => {
          console.error(error);
          setIsSaving(false);
        }
      );
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const hasSelectedRows = selectedEligibleRequests.length > 0 || selectedAddedRequests.length > 0;

  const handleClearSelected = () => {
    setSelectedEligibleRequests([]);
    setSelectedAddedRequests([]);
  };

  const handleRequestSave = (
    updatedRequest: ChatCompletionRequest,
    table: "eligible" | "added"
  ) => {
    if (table === "added") {
      setAddedRequests((prev) =>
        prev.map((request) =>
          request.request_id === updatedRequest.request_id
            ? updatedRequest
            : request
        )
      );

      if (addedRequestsCacheRef.current[currentAddedPage]) {
        addedRequestsCacheRef.current[currentAddedPage] =
          addedRequestsCacheRef.current[currentAddedPage].map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          );
      }
    } else if (table === "eligible") {
      setEligibleRequests((prev) =>
        prev.map((request) =>
          request.request_id === updatedRequest.request_id
            ? updatedRequest
            : request
        )
      );

      if (eligibleRequestsCacheRef.current[currentEligiblePage]) {
        eligibleRequestsCacheRef.current[currentEligiblePage] =
          eligibleRequestsCacheRef.current[currentEligiblePage].map((request) =>
            request.request_id === updatedRequest.request_id
              ? updatedRequest
              : request
          );
      }
    }
  };

  // Smart pagination component
  const SmartPagination: React.FC<{
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    itemName: string;
    totalItems: number;
    itemsPerPage: number;
  }> = ({ currentPage, totalPages, onPageChange, itemName, totalItems, itemsPerPage }) => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between px-6 py-4 border-t border-border">
        <div className="text-sm text-muted-foreground">
          Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} {itemName}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="gap-1"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Previous
          </Button>
          <div className="flex items-center gap-1">
            {[...Array(Math.min(totalPages, 5))].map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              const isCurrentPage = pageNum === currentPage;
              return (
                <Button
                  key={pageNum}
                  variant={isCurrentPage ? "default" : "ghost"}
                  size="sm"
                  onClick={() => onPageChange(pageNum)}
                  className={cn(
                    "w-8 h-8 p-0",
                    isCurrentPage && "bg-maitai-green text-white hover:bg-maitai-green/90"
                  )}
                >
                  {pageNum}
                </Button>
              );
            })}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="gap-1"
          >
            Next
            <ChevronRightIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  // Updated EligibleRequestsTable component
  const EligibleRequestsTable = () => {
    const totalEligiblePages = Math.ceil(totalEligible / eligiblePageSize);
    
    return (
      <TestSetEligibleRequestsTable
        requests={eligibleRequests}
        loading={isEligibleLoading}
        apiKey={apiKey}
        selectedRequests={selectedEligibleRequests}
        onSelectionChange={setSelectedEligibleRequests}
        onRequestUpdate={(request: ChatCompletionRequest) => handleRequestSave(request, "eligible")}
        selectable={true}
        showOnlySelected={showOnlySelectedEligible}
        currentPage={currentEligiblePage}
        totalPages={totalEligiblePages}
        totalItems={totalEligible}
        itemsPerPage={eligiblePageSize}
        onPageChange={setCurrentEligiblePage}
      />
    );
  };

  const AddedRequestsTable = () => {
    return (
      <TestSetAddedRequestsTable
        requests={addedRequests}
        apiKey={apiKey}
        title="Added Requests"
        showHeader={false}
        selectable={true}
        selectedRequests={selectedAddedRequests}
        onSelectionChange={setSelectedAddedRequests}
        showOnlySelected={showOnlySelectedAdded}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/intent-groups")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Intent Groups
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/intent-group/${testSet?.intent_group_id}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    {intentGroup?.intent_name}
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/intent-group/${intentGroupId}?tab=testsets`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Test Sets
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate(`/intent-group/${intentGroupId}/testsets/${testSetId}`)}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    {testSet?.name}
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Edit</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Edit Test Set
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Modify test set details and manage requests
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Form Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-gc-med text-foreground">Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter test set name"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-gc-med text-foreground">Golden</Label>
                    <Tooltip content="Test sets marked as 'Golden' will be used to evaluate the performance of models after fine-tuning">
                      <InfoIcon className="w-4 h-4 text-muted-foreground" />
                    </Tooltip>
                  </div>
                  <Switch
                    checked={isGolden}
                    onCheckedChange={setIsGolden}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-gc-med text-foreground">Description</Label>
                <textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter test set description"
                  rows={4}
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              {tagDistribution.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-gc-med text-foreground">Request Distribution</Label>
                  <div className="flex flex-wrap gap-2">
                    {tagDistribution.map(({ tag, count }) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400"
                      >
                        {tag} ({count})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tabs Card */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-8 py-4">
                <button
                  onClick={() => setActiveTab("eligible")}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                    activeTab === "eligible"
                      ? "bg-maitai-green/10 text-maitai-green"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  )}
                >
                  <PlusIcon className="w-4 h-4" />
                  Eligible Requests
                </button>
                <button
                  onClick={() => setActiveTab("added")}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                    activeTab === "added"
                      ? "bg-maitai-green/10 text-maitai-green"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                  )}
                >
                  <FileTextIcon className="w-4 h-4" />
                  Added Requests
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {activeTab === "eligible" ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-gc-med text-foreground">Eligible Requests</h3>
                      <p className="text-sm text-muted-foreground">Select requests to add to this test set</p>
                    </div>
                    {selectedEligibleRequests.length > 0 && (
                      <Button
                        onClick={handleAddSelected}
                        disabled={isAddedLoading}
                        className="gap-2"
                      >
                        {isAddedLoading && <Loader2Icon className="w-4 h-4 animate-spin" />}
                        Add Selected ({selectedEligibleRequests.length})
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={showOnlySelectedEligible}
                          onCheckedChange={setShowOnlySelectedEligible}
                        />
                        <span className="text-sm">Show Only Selected</span>
                      </div>
                    </div>
                  </div>

                  <EligibleRequestsTable />
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-gc-med text-foreground">Added Requests</h3>
                      <p className="text-sm text-muted-foreground">Manage requests in this test set</p>
                    </div>
                    {selectedAddedRequests.length > 0 && (
                      <Button
                        variant="destructive"
                        onClick={handleRemoveSelected}
                        disabled={isAddedLoading}
                        className="gap-2"
                      >
                        {isAddedLoading && <Loader2Icon className="w-4 h-4 animate-spin" />}
                        Remove Selected ({selectedAddedRequests.length})
                      </Button>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={showOnlySelectedAdded}
                          onCheckedChange={setShowOnlySelectedAdded}
                        />
                        <span className="text-sm">Show Only Selected</span>
                      </div>
                    </div>
                  </div>

                  <AddedRequestsTable />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3">
          {hasSelectedRows && (
            <Button onClick={handleClearSelected}>Clear Selected</Button>
          )}
          <Button
            variant="outline"
            onClick={() => navigate(`/intent-group/${testSet?.intent_group_id}/testsets/${testSetId}`)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          {hasSelectedRows ? (
            <Tooltip content="Please add or remove selected requests before saving changes">
              <div>
                <Button
                  onClick={handleSave}
                  disabled={hasSelectedRows || isSaving}
                  className="gap-2"
                >
                  {isSaving && <Loader2Icon className="w-4 h-4 animate-spin" />}
                  Save Changes
                </Button>
              </div>
            </Tooltip>
          ) : (
            <Button
              onClick={handleSave}
              disabled={hasSelectedRows || isSaving}
              className="gap-2"
            >
              {isSaving && <Loader2Icon className="w-4 h-4 animate-spin" />}
              Save Changes
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestSetEdit;
